import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { from, of } from 'rxjs';
import { flatMap, map, switchMap, tap, toArray } from 'rxjs/operators';
import { DNI } from 'src/app/shared/components/mapfre-update-dni/mapfre-update-dni.component';
import { DocumentCaptureRequest } from '../../models/document-capture';
import { fromEntries } from '../../utils/utils';
import { MMobileHelperService } from '../core/mmobile-helper.service';
import { CompressImageService } from '../helpers/compress-image.service';
import { DocumentCaptureTokenHttpService } from '../http-services/document-capture-token.service';
import { DocumentCaptureHttpService } from '../http-services/document-capture.service';

@Injectable({
  providedIn: 'root'
})
export class DocumentCaptureContextService {
  private tokenMap: Map<string, string> = new Map();

  constructor(
    private mmHelper: MMobileHelperService,
    private documentCaptureHttpService: DocumentCaptureHttpService,
    private documentCaptureTokenHttpService: DocumentCaptureTokenHttpService,
    private compressService: CompressImageService
  ) {
    DocumentCaptureContextService.build(
      this.mmHelper,
      this.documentCaptureHttpService
    );
  }

  static async build(
    mmHelper: MMobileHelperService,
    documentCaptureHttpService: DocumentCaptureHttpService
  ) {
    documentCaptureHttpService.init({
      baseUrl: await mmHelper.getServiceUrlSafe(
        'middlewarePrivateDocumentCaptureServices'
      ),
      versionId: await mmHelper.getVersionIdSafe()
    });
  }

  clear() {
    sessionStorage.removeItem('showDocumentCaptureNotification');
    this.tokenMap.clear();
  }

  getExpirationDateByDNI(documentNumber: string) {
    return this.getToken(documentNumber).pipe(
      switchMap(accessToken =>
        this.invokeGetExpirationDateByDNI(accessToken, documentNumber)
      )
    );
  }

  getExpirationDates() {
    return this.getTokens().pipe(
      switchMap(stateMap => from(Object.entries(stateMap))),
      flatMap(([accessToken, documentNumber]: [string, string]) =>
        this.invokeGetExpirationDateByDNI(accessToken, documentNumber)
      ),
      toArray()
    );
  }

  updateDNI(documentNumber: string, dni: DNI) {
    const body: DocumentCaptureRequest = {
      frontBase64: this.compressService.extractBase64BodyFromMetadata(
        dni.FRONT.base64
      ),
      backBase64: this.compressService.extractBase64BodyFromMetadata(
        dni.BACK.base64
      ),
      frontFormat: this.mimeTypeToFormatField(dni.FRONT.blob.type),
      backFormat: this.mimeTypeToFormatField(dni.BACK.blob.type)
    };

    return this.getToken(documentNumber).pipe(
      switchMap(accessToken =>
        this.documentCaptureHttpService.updateDNI(accessToken, body)
      )
    );
  }

  private getToken(documentNumber: string) {
    return this.getTokens().pipe(
      map(tokensByDoc => tokensByDoc.get(documentNumber))
    );
  }

  private getTokens() {
    return !this.isStateEmpty() && !this.hasSomeTokensExpired(this.tokenMap)
      ? of(this.tokenMap)
      : this.invokeGetTokensCachePut();
  }

  private invokeGetTokensCachePut() {
    return this.documentCaptureTokenHttpService.getTokens().pipe(
      map(tokens => {
        this.clear();
        tokens.forEach(({ documentNumber, accessToken }) =>
          this.updateToken(documentNumber, accessToken)
        );
        return this.tokenMap;
      })
    );
  }

  private invokeGetExpirationDateByDNI(token: string, documentNumber: string) {
    return this.documentCaptureHttpService
      .getDocuments(token)
      .pipe(map(({ expirationDate }) => ({ documentNumber, expirationDate })));
  }

  private mimeTypeToFormatField(mimeType) {
    return mimeType.replace('image/', '').replace('jpeg', 'jpg').toUpperCase();
  }

  private hasSomeTokensExpired(stateMap: Map<string, string>) {
    const jwt = new JwtHelperService();
    return Array.from(stateMap.values()).some(accessToken =>
      jwt.isTokenExpired(accessToken)
    );
  }

  private isStateEmpty() {
    return this.tokenMap.size === 0;
  }

  private updateToken(documentNumber: string, token: string) {
    this.tokenMap.set(documentNumber, token);
  }
}
